// ---------------------------------------------------------------------------
// YouAreYou (uru) — DocsAreSigned link tool (/documents)
//
// 2026 rose refresh — matches screen-mockups/redesign/docsaresigned_youareyou.
// Flow: create the signing request in the MojoVerify Admin Portal → paste its
// link (or UUID) here → get a branded signer link + youru.io short link.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import { createSigningShortlink, listGroups, checkKycStatus } from "@/api/endpoints";
import type { KycStatusData } from "@/types/api";
import { formatRelativeTime } from "@/lib/utils";
import Icon from "@/components/ui/Icon";
import BrandMark from "@/components/ui/BrandMark";
import BrandName from "@/components/ui/BrandName";
import DocsBrandName from "@/components/ui/DocsBrandName";

const APP_URL = String(import.meta.env.VITE_APP_URL ?? window.location.origin).replace(/\/$/, "");
const PORTAL_URL = String(
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_PORTAL_URL ||
    "https://mojoverify.com/portal",
).replace(/\/$/, "");

const CARD_SHADOW = "shadow-[0px_4px_20px_rgba(26,31,43,0.04)]";

// Host that serves the recipient-facing verify pages (basic form + receipt).
const VERIFY_BASE = String(
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_VERIFY_BASE_URL ||
    "https://mojoverify.com",
).replace(/\/$/, "");

function receiptUrlFor(uuid: string): string {
  return `${VERIFY_BASE}/verify/receipt/${uuid}`;
}

// ---- Receipt status, normalized from the live check-status payload ----
// docs/api.md documents `verification_status`, but the deployed endpoint returns
// the full PVR with `status` + `completed_at`. `completed_at` is authoritative.
type DerivedStatus = "complete" | "in_progress" | "pending" | "failed" | "unknown";

function deriveStatus(d: KycStatusData): DerivedStatus {
  if (d.completed_at) return "complete";
  const s = (d.status ?? d.verification_status ?? "").toLowerCase();
  if (s === "completed") return "complete";
  if (s.includes("fail") || s.includes("reject") || s.includes("denied") || s.includes("expired"))
    return "failed";
  if (s.includes("collecting") || s.includes("progress") || s.includes("processing"))
    return "in_progress";
  if (s === "pending" || s === "") return "pending";
  return "in_progress";
}

const STATUS_CHIP: Record<DerivedStatus, { label: string; cls: string; icon: string }> = {
  complete: { label: "Complete", cls: "bg-[#e8f3ec] text-[#2e7d4f]", icon: "task_alt" },
  in_progress: { label: "In progress", cls: "bg-[#fef3e0] text-[#b06d00]", icon: "hourglass_top" },
  pending: { label: "Not yet complete", cls: "bg-[#f1edf2] text-[#6b5a6b]", icon: "schedule" },
  failed: { label: "Failed", cls: "bg-[#fde7e7] text-[#ba1a1a]", icon: "error" },
  unknown: { label: "Unknown", cls: "bg-[#f1edf2] text-[#877271]", icon: "help" },
};

// Deep link to the admin portal's signing-requests page, scoped to the user's group.
function portalRequestsUrl(groupId?: number): string {
  const params = new URLSearchParams({ page: "group/docsign/requests", size: "25", sort: "-created" });
  if (groupId != null) params.set("group", String(groupId));
  return `${PORTAL_URL}/?${params.toString()}`;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function extractUuid(input: string): string | null {
  const m = input.trim().match(UUID_RE);
  return m ? m[0].toLowerCase() : null;
}

// ---------------------------------------------------------------------------
// Local history — a persistent feed of signing links minted on this device.
// Docsign has no completion-status API yet (blocked on Ian's /api/uru/me/docsign
// wrappers), so this is link bookkeeping only — no status chip.
// ---------------------------------------------------------------------------

const HISTORY_KEY = "uru_docsign_links";

interface DocLinkEntry {
  uuid: string;
  signerUrl: string;
  shortUrl?: string;
  createdAt: number; // epoch ms
}

function loadHistory(): DocLinkEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is DocLinkEntry =>
          !!e &&
          typeof e === "object" &&
          typeof (e as DocLinkEntry).uuid === "string" &&
          typeof (e as DocLinkEntry).signerUrl === "string",
      )
      .map((e) => {
        // Canonicalize legacy signer links to the current /id/{uuid} route.
        // Earlier builds used /sign/ (docsign) then /verify/ (nginx-proxied → 404).
        // Their short links wrap the dead path server-side, so drop them.
        const isLegacy = e.signerUrl.includes("/sign/") || e.signerUrl.includes("/verify/");
        if (!isLegacy) return e;
        return { ...e, signerUrl: `${APP_URL}/id/${e.uuid}`, shortUrl: undefined };
      });
  } catch {
    return [];
  }
}

function saveHistory(entries: DocLinkEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}

// Copy-to-clipboard with transient feedback.
function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          },
          () => {},
        );
      }}
      className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-white transition-colors ${
        copied ? "bg-[#74a57f]" : "bg-[#f08b8b] hover:bg-[#d97a7c]"
      }`}
    >
      <Icon name={copied ? "check" : "content_copy"} className="text-sm" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function LinkRow({ label, url }: { label: string; url: string }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#544242]">{label}</p>
      <div className="flex items-stretch gap-2">
        <div className="flex min-w-0 flex-1 items-center rounded-lg bg-[#faf9ff] px-3 py-2">
          <Icon name="link" className="mr-2 shrink-0 text-base text-[#877271]" />
          <span className="truncate font-mono text-xs text-[#544242]">{url}</span>
        </div>
        <CopyButton value={url} />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#dac1c0] bg-white px-3 py-2 text-xs font-semibold text-[#544242] transition-colors hover:bg-[#faf9ff]"
        >
          <Icon name="open_in_new" className="text-sm" />
          Open
        </a>
      </div>
    </div>
  );
}

type ShortState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "done"; url: string }
  | { kind: "error"; message: string };

// A compact link cell: label, truncated URL, copy + open actions.
function LinkCell({ label, url }: { label: string; url: string }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#877271]">{label}</p>
      <div className="flex items-center gap-1.5">
        <div className="flex min-w-0 flex-1 items-center rounded-lg bg-[#faf9ff] px-2.5 py-1.5">
          <span className="truncate font-mono text-xs text-[#544242]">
            {url.replace(/^https?:\/\//, "")}
          </span>
        </div>
        <CopyButton value={url} />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${label}`}
          className="flex shrink-0 items-center rounded-lg border border-[#dac1c0] bg-white px-2 py-2 text-[#544242] transition-colors hover:bg-[#faf9ff]"
        >
          <Icon name="open_in_new" className="text-sm" />
        </a>
      </div>
    </div>
  );
}

type StatusState =
  | { kind: "loading" }
  | { kind: "loaded"; status: DerivedStatus; data: KycStatusData }
  | { kind: "error" };

// A single row in the recent-documents feed: Sent | Receipt (+ live status).
function HistoryRow({
  entry,
  onRemove,
}: {
  entry: DocLinkEntry;
  onRemove: (uuid: string) => void;
}) {
  const sentUrl = entry.shortUrl ?? entry.signerUrl;
  const [status, setStatus] = useState<StatusState>({ kind: "loading" });

  const refresh = useCallback(() => {
    setStatus({ kind: "loading" });
    checkKycStatus(entry.uuid).then(
      (data) => setStatus({ kind: "loaded", status: deriveStatus(data), data }),
      () => setStatus({ kind: "error" }),
    );
  }, [entry.uuid]);

  useEffect(() => {
    let cancelled = false;
    checkKycStatus(entry.uuid).then(
      (data) => {
        if (!cancelled) setStatus({ kind: "loaded", status: deriveStatus(data), data });
      },
      () => {
        if (!cancelled) setStatus({ kind: "error" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [entry.uuid]);

  // Prefer the receipt link the API returns; fall back to the derived one.
  const receiptUrl =
    status.kind === "loaded" && status.data.receipt_link
      ? status.data.receipt_link
      : receiptUrlFor(entry.uuid);

  const chip = status.kind === "loaded" ? STATUS_CHIP[status.status] : null;

  return (
    <div className={`rounded-2xl bg-white p-4 ${CARD_SHADOW}`}>
      {/* Header: identity + time + remove */}
      <div className="mb-3 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fef0f0]">
          <Icon name="draft" className="text-base text-[#974446]" />
        </div>
        <p className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-[#877271]">
          <span className="font-mono">{entry.uuid.slice(0, 8)}</span>
          <span>·</span>
          <span>{formatRelativeTime(entry.createdAt)}</span>
        </p>

        {/* Status pill */}
        {status.kind === "loading" && (
          <span className="flex items-center gap-1 rounded-full bg-[#f1edf2] px-2 py-0.5 text-[11px] font-semibold text-[#877271]">
            <Icon name="progress_activity" className="animate-spin text-xs" />
            Checking…
          </span>
        )}
        {status.kind === "error" && (
          <button
            onClick={refresh}
            className="flex items-center gap-1 rounded-full bg-[#fde7e7] px-2 py-0.5 text-[11px] font-semibold text-[#ba1a1a]"
          >
            <Icon name="refresh" className="text-xs" />
            Retry
          </button>
        )}
        {chip && (
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${chip.cls}`}>
            <Icon name={chip.icon} className="text-xs" />
            {chip.label}
          </span>
        )}
        {status.kind === "loaded" && (
          <button
            onClick={refresh}
            aria-label="Refresh status"
            className="flex shrink-0 items-center rounded-lg px-1 py-1 text-[#877271] transition-colors hover:text-[#974446]"
          >
            <Icon name="refresh" className="text-sm" />
          </button>
        )}

        <button
          type="button"
          onClick={() => onRemove(entry.uuid)}
          aria-label="Remove from history"
          className="flex shrink-0 items-center rounded-lg px-1 py-1 text-[#877271] transition-colors hover:text-[#ba1a1a]"
        >
          <Icon name="close" className="text-base" />
        </button>
      </div>

      {/* Two columns: Sent | Receipt */}
      <div className="grid gap-3 sm:grid-cols-2">
        <LinkCell label="Sent" url={sentUrl} />
        <LinkCell label="Receipt" url={receiptUrl} />
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const [input, setInput] = useState("");
  const [short, setShort] = useState<ShortState>({ kind: "idle" });
  const [adminGroupId, setAdminGroupId] = useState<number | undefined>(undefined);
  const [history, setHistory] = useState<DocLinkEntry[]>(() => loadHistory());

  // Insert or update a history entry keyed by UUID, newest first, then persist.
  const upsertHistory = useCallback((entry: DocLinkEntry) => {
    setHistory((prev) => {
      const existing = prev.find((e) => e.uuid === entry.uuid);
      const merged = existing ? { ...existing, ...entry } : entry;
      const next = [merged, ...prev.filter((e) => e.uuid !== entry.uuid)];
      saveHistory(next);
      return next;
    });
  }, []);

  const removeHistory = useCallback((uuid: string) => {
    setHistory((prev) => {
      const next = prev.filter((e) => e.uuid !== uuid);
      saveHistory(next);
      return next;
    });
  }, []);

  // Discover the user's admin/org group for the portal deep link.
  useEffect(() => {
    let cancelled = false;
    listGroups().then(
      (groups) => {
        if (cancelled) return;
        const admin = groups.find((g) => g.kind !== "uru") ?? groups[0];
        if (admin) setAdminGroupId(admin.id);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const uuid = useMemo(() => extractUuid(input), [input]);
  // /id/ (not /verify/) — nginx proxies /verify/* to the backend, so a /verify
  // SPA route 404s on direct load for recipients.
  const uruLink = uuid ? `${APP_URL}/id/${uuid}` : null;

  const makeShort = () => {
    if (!uruLink || !uuid) return;
    setShort({ kind: "loading" });
    // Log the document immediately so it's saved even if shortening fails.
    upsertHistory({ uuid, signerUrl: uruLink, createdAt: Date.now() });
    createSigningShortlink(uruLink).then(
      (res) => {
        setShort({ kind: "done", url: res.short_link });
        upsertHistory({ uuid, signerUrl: uruLink, shortUrl: res.short_link, createdAt: Date.now() });
      },
      (err: unknown) =>
        setShort({
          kind: "error",
          message:
            err instanceof Error ? err.message : "Couldn't create a short link — use the full link above.",
        }),
    );
  };

  return (
    <div className="min-h-full bg-[#fef0f0]">
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        {/* Hero */}
        <header className="mb-8 text-center">
          <BrandMark className="mx-auto mb-4 h-10 w-10" />
          <div className="text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
            <DocsBrandName showBadge={false} />
          </div>
          <p className="mt-2 text-sm text-[#544242]">
            powered by <BrandName showBadge={false} />
          </p>
        </header>

        {/* Monolithic card */}
        <div className={`rounded-2xl bg-white p-6 sm:p-8 ${CARD_SHADOW}`}>
          {/* Admin Portal tip */}
          <div className="mb-6 flex items-start gap-2 rounded-lg bg-[#fef0f0] px-4 py-3 text-sm text-[#544242]">
            <Icon name="info" className="mt-0.5 shrink-0 text-lg text-[#974446]" />
            <p>
              <span className="font-semibold text-[#161b27]">Admin Portal:</span> create the signing request
              there (open a customer → Send Document for Signing), then paste its link or ID below.{" "}
              <a
                href={portalRequestsUrl(adminGroupId)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-semibold text-[#974446] underline decoration-[#dac1c0] underline-offset-2 hover:text-[#792e30]"
              >
                Open the portal
                <Icon name="open_in_new" className="text-sm" />
              </a>
            </p>
          </div>

          {/* Input */}
          <label htmlFor="docsign-input" className="mb-1.5 block text-sm font-semibold text-[#161b27]">
            Signing Link or Document UUID
          </label>
          <textarea
            id="docsign-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShort({ kind: "idle" });
            }}
            rows={2}
            placeholder="Paste your signing link or unique signing UUID here…"
            className="w-full resize-none rounded-lg border border-[#dac1c0] bg-white px-3 py-2.5 text-sm text-[#161b27] placeholder:text-[#877271] focus:border-[#f08b8b] focus:outline-none focus:ring-4 focus:ring-[#f08b8b]/15"
          />

          {input.trim() && !uuid && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-[#ba1a1a]">
              <Icon name="error" className="text-sm" />
              Couldn't find a signing ID in that — paste the full signing link or the request UUID.
            </p>
          )}

          {/* Branded signer link */}
          {uruLink && (
            <div className="mt-5 space-y-4">
              <LinkRow label="YouAreYou signer link" url={uruLink} />
              {short.kind === "done" && <LinkRow label="Short link" url={short.url} />}
            </div>
          )}

          {/* Primary CTA */}
          <button
            onClick={makeShort}
            disabled={!uruLink || short.kind === "loading"}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-[#f08b8b] py-4 text-base font-bold text-white shadow-[0px_8px_32px_rgba(240,139,139,0.25)] transition-all hover:bg-[#d97a7c] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:translate-y-0"
          >
            <Icon name="auto_fix_high" className="text-xl" />
            {short.kind === "loading" ? "Creating…" : "Create short link"}
          </button>

          {short.kind === "error" && (
            <p className="mt-3 text-center text-xs text-[#544242]">{short.message}</p>
          )}

          {uuid && (
            <p className="mt-4 text-center text-xs text-[#877271]">
              Detected request ID <span className="font-mono">{uuid}</span>
            </p>
          )}
        </div>

        {/* Recent documents — persistent local history of links minted here */}
        {history.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex items-center justify-between px-1">
              <h2 className="text-sm font-bold uppercase tracking-wide text-[#544242]">
                Recent documents
              </h2>
              <span className="text-xs text-[#877271]">
                {history.length} saved on this device
              </span>
            </div>
            <div className="space-y-2.5">
              {history.map((entry) => (
                <HistoryRow key={entry.uuid} entry={entry} onRemove={removeHistory} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
